<template>
  <VDialogV3
    id="order-card"
    ref="dialog"
    position="bottom"

    :entity="localOrder"
    :submit-method="submit"

    show-edit-button
    :errors="dialogErrors"
    :context-menu="OrdersService.contextMenu"
    :on-context-menu-action="onContextMenuAction"
    :file-entities="filesPointers"
    :file-entities-inherited="inheritedFilesPointers"

    @edit-button:clicked="showRequisitesDialog"
  >
    <template #badges>
      <VBadge v-if="localOrder.requisites.isSamples === true">Образец</VBadge>
      <VBadge v-if="localOrder.requisites.isPreorder === true">Предзаказ</VBadge>
      <VBadge v-if="localOrder.isCopy === true">Копия</VBadge>
      <VBadge v-if="localOrder.notificationSentAt">Уведомление отправлено {{ $core.utils.date.humanizeDate(localOrder.notificationSentAt, 'HH|mm') }}</VBadge>
    </template>

    <header class="document-header">
      <dl>
        <dt>Продавец</dt>
        <dd v-if="localOrder.requisites.sellerCompany">{{ localOrder.requisites.sellerCompany?.name}} ({{ localOrder.requisites.seller.name }})</dd><dd v-else>Не выбран</dd>
        <dt>Склад отправки</dt>
        <dd>{{ localOrder.requisites.sellerStore?.address }}</dd>
      </dl>

      <dl>
        <dt>Покупатель</dt>
        <dd v-if="localOrder.requisites.buyerCompany?.name">{{ localOrder.requisites.buyerCompany?.name }} ( {{ localOrder.requisites.buyer?.name }} )</dd><dd v-else>Не выбран</dd>
        <dt>Склад доставки</dt>
        <dd>{{ localOrder.requisites.buyerStore?.address }}</dd>
      </dl>
    </header>

    <header class="document-header">
      <dl>
        <dt>Договор</dt>
        <dd>{{ localOrder.requisites.contract?.toString() ?? '--' }}</dd>
        <dt>Оплата</dt>
        <dd>{{ localOrder.requisites.paymentTermsString }}</dd>
        <dt>Доставка</dt>
        <dd>{{ localOrder.requisites.deliveryString }}</dd>
      </dl>

      <dl>
        <dt>Ответственный</dt>
        <dd>{{ localOrder.requisites.responsible?.name ?? '--' }}</dd>
        <template v-if="localOrder.requisites.isPreorder === true">
          <dt>Категория</dt>
          <dd>{{ localOrder.requisites.orderCategory?.name ?? '--' }}</dd>
        </template>
        <template v-else>
          <dt>Комментарий</dt>
          <dd>{{ localOrder.notes ?? 'Не указан' }}</dd>
        </template>
        <dt>НДС в цене</dt>
        <dd>{{ localOrder.requisites.vatIncludedSellerString }}</dd>
      </dl>

      <aside class="totals">
        <section>
          <header>
            В валюте сделки
            <template v-if="localOrder.sumFullCalculated?.isEmpty() || localOrder.sumFullCalculated === null">--</template>
            <VLink v-else v-if="localOrder.isMultiCurrency === true" class="sum-block-button" @click="openSumBlockPopup($event)" material="info"/>
          </header>
          <dl>
            <dt class="total">Итого</dt>
            <dd class="total">{{ localOrder.sumFullCalculated?.format(localOrder.currency) || '--' }}</dd>

            <dt>НДС {{ localOrder.requisites.vatIncludedSellerString }}</dt>
            <dd>{{ localOrder.vatSumCalculated?.format(localOrder.currency) || '--' }}</dd>
          </dl>
        </section>

        <section>
          <header>
            В валюте учёта, курс
            <template v-if="localOrder.accountingSumFullCalculated?.isZero()">--</template>
            <template v-else>
              <template v-if="localOrder.isMultiCurrency === true">
                <VLink @click="openAccountingSumBlockPopup($event)" material="info"/>
              </template>
              <template v-else>
                {{ localOrder.accountingCurrencyRate?.format() || '--' }}
              </template>
            </template>
          </header>
          <dl>
            <dt class="total">Итого</dt>
            <dd class="total">{{ localOrder.accountingSumFullCalculated?.format(localOrder.accountingCurrency) || '--' }}</dd>

            <dt>
              <template v-if="localOrder.requisites.sellerCompany?.isVatPayer === true">
                НДС в т.ч:
              </template>
              <template v-else>
                НДС не облагается
              </template>
            </dt>
            <dd v-if="localOrder.requisites.sellerCompany?.isVatPayer === true">
              {{ localOrder.accountingVatSumCalculated?.format(localOrder.accountingCurrency) || '--' }}
            </dd>
          </dl>
        </section>
      </aside>
    </header>

    <VDataGridV2
      class="fill"
      :data="localOrder?.positions"
      @row:dblclick="(row) => showOrderPositionDialog(row)"
    >

      <VDataGridColumnV2
        id="position-action"
        width="40px"
        content-align="right"
      >
        <template #default="{ row }">
          <CommentsButton
            :entity="row"
            :entity-type-name="localOrder.requisites.isPreorder ? 'preorderPosition' : 'orderPosition'"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="filter-code"
        width="94px"
        content-align="center"
      >
        <template #label>
          <div>Арт</div>
          <span v-if="localOrder.isExisting === true" class="description">№</span>
        </template>
        <template #default="{ row }">
          <VLink @click="OrdersService.showCodeInfoPopup(row, $event.currentTarget)">
            {{ row.product.code || '--' }}
          </VLink>
          <div class="description">
            {{ $core.utils.string.formatWithDelimiter(row.id) }}
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="category"
        content-align="left"
      >
        <template #label>
          <div>Товар</div>
          <div class="description">
            <template v-if="localOrder.requisites.isRawMaterialsAndPackage === true">
              Производитель
            </template>
            <template v-if="localOrder.requisites.isPrinting === true">
              Материал
            </template>
          </div>
        </template>
        <template #default="{ row }">
          <span
            @mouseenter="$core.tooltip.show($event.currentTarget, row.name)"
            @mouseleave="$core.tooltip.hide()"
          >
            {{ row.name }}
          </span>
          <div class="description">
            {{ row.description }}
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        v-if="localOrder.requisites.isPrinting === true"
        id="status"
        width="50px"
        content-align="left"
        content-class="form"
      >
        <template #label>
          <div>Согласовано</div>
          <VLink
            class="mass-date-link"
            :disabled="$core.utils.isNotEmptyArray(localOrder.positions) === false"
            @click="localOrder.setApprove(true)"
          >
            Согласовать всё
          </VLink>
        </template>
        <template #default="{ row }">
          <VSwitch
            v-model="row.isApproved"
            theme="slider"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="qty"
        width="140px"
        content-align="right"
        label="Кол-во"
        content-class="form"
      >
        <template #default="{ row, index }">
          <VInputNumberRusV2
            v-model="row.qty"
            :type="Qty"
            autocomplete="off"
            :data-position-index="index"
            :unit="row.unit"
            required
            :warning-method="(value) => validateQty(value, row)"
          >
            <template
              #helper-text
              v-if="localOrder.requisites.isRawMaterialsAndPackage === true"
            >
              Фасовка: {{ row.packageVolume || '--' }} {{ row.unit }}
            </template>
          </VInputNumberRusV2>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="price"
        width="150px"
        content-align="right"
        content-class="form"
      >
        <template #label>
          Цена
          <div class="description">Сумма</div>
        </template>
        <template #default="{ row }">
          <VInputNumberRusV2
            v-model="row.priceSeller"
            :type="Price"
            class="price-input"
            autocomplete="off"
            :disabled="localOrder.isFreeSamples"
            :unit="localOrder.isFreeSamples ? '' : `${row.currency.symbol} / ${row.unit}`"
            required
          >
            <template #helper-text>
              {{ row.sumSeller?.format(row.currency) || '--' }}
            </template>
          </VInputNumberRusV2>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="price-full"
        width="150px"
        content-align="right"
      >
        <template #label>
          Цена полная
          <div class="description">Сумма</div>
        </template>
        <template #default="{ row }">
          {{ row.priceFullSeller?.format(row.currency, row.unit) || '--' }}

          <div class="description">
            {{ row.sumFullSeller?.format(row.currency) || '--' }}
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="receipt-date"
        width="150px"
        content-class="form"
      >
        <template #label>
          <template v-if="localOrder.requisites.isPreorder === true">
            Период доставки
          </template>
          <template v-else>
            Дата доставки
          </template>

          <VLink
            class="mass-date-link description"
            :disabled="$core.utils.isNotEmptyArray(localOrder.positions) === false"
            @click="showSetDeliveryDatesDialog()"
          >
            Установить общую дату
          </VLink>
        </template>
        <template #default="{ row }">
          <div :class="{'preorder-shipment-period' : localOrder.requisites.isPreorder === true}">
            <template v-if="localOrder.requisites.isPreorder === true">
                <span v-if="row.preorderShipmentPeriod?.isFilled() === true">
                  {{ row.preorderShipmentPeriod.toString({ prefix: 'с', middle: 'по' }) }}
                </span>
              <span v-else class="danger">Не установлен</span>
            </template>
            <template v-else>
              <VDateInput
                v-model="row.desiredReceiptDate"
                class="desired-receipt-date-input receipt-date"
                required
                :validate-method="row.validateDesiredReceiptDate.bind(row)"
                :warning-method="warnWeekend"
              >
                <template #helper-text>
                  {{ weekDayHelper(row.desiredReceiptDate) }}
                </template>
              </VDateInput>
            </template>
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        v-if="localOrder.requisites.isPreorder === false"
        id="shipment-date"
        width="140px"
        content-class="form"
      >
        <template #label>
          Отгрузка план
        </template>
        <template #default="{ row }">
          <VDateInput
            v-model="row.plannedShipmentDate"
            required
            :warning-method="warnWeekend"
          >
            <template #helper-text>
              {{ weekDayHelper(row.plannedShipmentDate) }}
            </template>
          </VDateInput>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="actions"
        width="40px"
        content-align="right"
        content-class="form"
      >
        <template #default="{ row }">
          <VContextMenuButton theme="default">
            <div class="popup-menu">
              <VButton
                material="open_in_new"
                @click="showOrderPositionDialog(row)"
              >Открыть</VButton>

              <hr>

              <VButton
                material="delete"
                @click="localOrder.removePosition(row)"
              >
                Удалить
              </VButton>
            </div>
          </VContextMenuButton>
        </template>
      </VDataGridColumnV2>

      <VDataGridActionBarV2
        v-if="showFooter === true"
        class="centered"
        :class="{ empty: $core.utils.isEmptyArray(localOrder.positions) }"
      >
        <VLink
          icon-postfix
          @click="showOrderPositionDialog()"
          material="add"
        >
          Добавить позицию
        </VLink>

        <VLink
          icon-postfix
          @click="showOrderPositionsRegistryDialog"
          material="add"
        >
          Из истории
        </VLink>

        <VLink
          v-if="localOrder.requisites.isPrinting === true"
          icon-postfix
          @click="onStartImport"
          material="add"
        >
          Импортировать
        </VLink>

      </VDataGridActionBarV2>
    </VDataGridV2>


    <template #footer>
      <!-- футер свой: блокировку по плашке ошибок VDialogV3 ставит только на стандартную кнопку -->
      <VButton
        theme="primary"
        :disabled="dialogErrors.length > 0"
        @click="$refs.dialog.submit()"
      >
        Сохранить
      </VButton>

      <VButton
        theme="primary"
        @click="onConfirmButtonClicked"
        :disabled="localOrder.getReasonCannotApprove() !== null"
        @mouseenter="$core.tooltip.show($event.target, localOrder.getReasonCannotApprove())"
        @mouseleave="$core.tooltip.hide()"
      >
        Утвердить
      </VButton>

      <VButton
        @click="OrdersService.openNotifyPersonsDialog(localOrder)"
        :disabled="localOrder.getReasonCannotNotify() !== null"
        @mouseenter="$core.tooltip.show($event.target, localOrder.getReasonCannotNotify())"
        @mouseleave="$core.tooltip.hide()"
      >
        Уведомить поставщика
      </VButton>

      <VButton @click="$core.dialogs.OrderCard.hide()">Отмена</VButton>
    </template>
  </VDialogV3>
</template>

<script lang="ts">

import OrdersService                    from '@/domains/Orders/OrdersService';
import Order                            from '@/domains/Orders/Order';
import OrderPosition                    from '@/domains/Orders/OrderPosition';
import Price                            from "@/core/types/Price";
import Qty                              from "@/core/types/Qty";
import OrderCategory, {OrderCategoryId} from "@/domains/Orders/OrderCategory";
import Preorder                         from "@/domains/Preorders/Preorder";
import PartnersService from "@/domains/Partners/PartnersService";
import EntityPointer                    from '@/domains/EntityPointer/EntityPointer';
import type PreorderPosition from '@/domains/Preorders/PreorderPosition';

export default defineComponent({
  name: 'OrderCard',

  props: {
    /** ID заказа — передаётся из роутера, карточка сама загружает объект */
    id: {
      type: Number,
      default: null,
    },

    /** тип заказа для создания нового */
    newOrderType: {
      type: String as PropType<OrderCategoryId>,
      default: null,
    },

    /** Заказ для копирования */
    orderIdToCopy: {
      type: Number,
      default: null,
    },

    isPreorder: {
      type: Boolean,
      default: false
    },

    filterParams: {
      type: Object,
      default: null
    }
  },

  setup() {
    return {
      OrdersService,
      Price,
      Qty
    }
  },

  data() {
    return {
      localOrder: null as Order,
    };
  },

  async created() {
    await this.init();
  },

  computed: {
    /**
     * Сущности, в которые грузятся файлы из скрепки: только сам заказ.
     * Файлы доступны только покупателю — иначе null, скрепки нет.
     */
    filesPointers(): EntityPointer[] | null {
      if (this.$core.currentPartner.isBuyer) {
        return EntityPointer.collect([
          EntityPointer.make('order', this.localOrder?.id, this.localOrder?.toString()),
        ]);
      } else {
        return null;
      }
    },

    /**
     * Унаследованные файлы заказа — тот же набор, что у пункта меню «Файлы».
     */
    inheritedFilesPointers(): EntityPointer[] {
      if (this.$core.utils.isNullOrUndefined(this.localOrder) === false) {
        return OrdersService.getInheritedFilesPointers(this.localOrder);
      } else {
        // заказ ещё не загружен
        return [];
      }
    },

    positionsErrorText(): string | null {
      if (!this.localOrder) return;

      if (this.$core.utils.isNotEmptyArray(this.localOrder.positions) === false) {
        return 'Нельзя сохранить заказ без позиций';
      }

      // FIXME тут нет такого условия как ниже для намангана, почему?
      if (this.localOrder.requisites.isPrinting === false) {
        if (this.positionsStoreTemperatureEmptyError === true) {
          return 'Нельзя сохранить с незаполненными температурными режимами';
        }
        if (this.positionsHaveDesiredTimeError === true && this.localOrder.requisites.isPreorder === true) {
          return 'У некоторых позиций отсутствует желаемый период получения';
        }
        if (this.localOrder.requisites.isPreorder === true && this.localOrder.positions.length > 1) {
          return 'Прямой предзаказ может иметь только одну позицию';
        }
      }

      return null;
    },

    dialogErrors(): { message: string }[] {
      if (this.positionsErrorText !== null) {
        return [{ message: this.positionsErrorText }];
      }
      else {
        return [];
      }
    },

    isDemandV2Based(): boolean {
      if (this.$core.utils.isNotEmptyArray(this.localOrder.positions) === false) {
        return false;
      }

      return this.localOrder.positions[0].demandV2?.id !== undefined;
    },

    positionsStoreTemperatureEmptyError(): boolean {
      // снятия обязательности проверки температурного диапазона, типа... у шланга поливочного температуры хранения нет
      // переделать потом
      if (this.isDemandV2Based === true) {
        return false;
      }

      return this.localOrder.positions.some(
        position => position.storeTemperatureRange !== null
          && position.storeTemperatureRange.isFilled() === false,
      );
    },

    positionsHaveDesiredTimeError(): boolean {
      if (this.$core.utils.isNotEmptyArray(this.localOrder.positions) === false || this.localOrder.requisites.isPreorder === false) {
        return false;
      }
      if (this.localOrder.positions[0].preorderShipmentPeriod === null) {
        return false;
      }

      return this.localOrder.positions.some(position => position.preorderShipmentPeriod?.isFilled() === false);
    },

    showFooter(): boolean {
      /** Если это НЕ предзаказ, то все равно сколько будет позиций в заказке */
      if (this.localOrder.requisites.isPreorder === false) {
        return true;
      }
      /** Если это предзаказ, то должна быть ОДНА позиция, это важно для isOfferBased = false */
      return this.localOrder.positions.length < 1;
    },
  },

  methods: {
    async init(): Promise<void> {
      if (this.id) {
        this.localOrder = await OrdersService.getOrder(this.id) as Order;
      }
      else if (this.orderIdToCopy) {
        const order = await OrdersService.getOrder(this.orderIdToCopy) as Order;

        this.localOrder = Order.fromAnotherOrder(order);
      }
      else if (this.newOrderType) {
        if (this.isPreorder === true) {
          this.localOrder = Preorder.fromNew(OrderCategory.ALL.get(this.newOrderType)!);
        } else {
          this.localOrder = Order.fromNew(OrderCategory.ALL.get(this.newOrderType)!);
        }

        // получаем id поставщика из фильтра
        const sellerId = this.getFilterParamValue('seller')?.id;

        if (sellerId === null || sellerId === undefined) {
          // поставщик в фильтре не выбран, ничего не делаем
        }
        else {
          const seller = await PartnersService.get(sellerId);

          if (seller !== null) {
            this.localOrder.requisites.seller = seller;
          }
        }

      }
      else {
        throw new Error('OrderCard открыта без источника: нужен id, orderIdToCopy или newOrderType');
      }

      if (this.localOrder.isExisting === false) {
        this.showRequisitesDialog();
      }
    },

    async submit(): Promise<boolean> {
      let confirmText: string;
      let isSubmitted = false;

      if (this.localOrder.requisites.isPreorder === true) {
        confirmText = 'Сохранить предзаказ?';
      }
      else {
        confirmText = 'Сохранить черновик заказа?';
      }

      if (await this.$core.confirm(confirmText) === true) {
        if (this.localOrder.isExisting === true) {
          isSubmitted = await OrdersService.updateOrder(this.localOrder);
        }
        else {
          isSubmitted = await OrdersService.add(this.localOrder);
        }
      }
      else {
        // пользователь передумал — карточка остаётся открытой
      }

      return isSubmitted;
    },

    /**
     * Пункт меню что-то изменил: удалённый заказ закрываем (хост получит true
     * и обновится), остальное — перечитываем.
     */
    async onContextMenuAction(): Promise<void> {
      if (this.localOrder.isDeleted === true) {
        this.$core.dialogs.OrderCard.resolve();
      }
      else {
        this.localOrder = await OrdersService.getOrder(this.localOrder.id) as Order;
      }
    },

    /**
     * Открыть диалог редактирования позиции. Без аргумента — создание новой
     * (диалог сам сделает OrderPosition.fromOrder), с позицией — редактирование.
     */
    showOrderPositionDialog(position: OrderPosition | PreorderPosition | undefined = undefined): void {
      this.$core.dialogs.OrderPositionRequisites.show(
        {
          position,
          order: this.localOrder,
          showReasonInput: false,
        },
        {
          save: ({ position: edited }: { position: OrderPosition }) => {
            const index = position === undefined ? -1 : this.localOrder.positions.indexOf(position);

            if (index === -1) {
              this.localOrder.addPosition(edited);
            }
            else {
              this.localOrder.positions = this.localOrder.positions.with(index, edited);
            }

            this.$core.dialogs.OrderPositionRequisites.hide();
          },
        },
      );
    },

    showRequisitesDialog(): void {
      this.$core.dialogs.OrderRequisites.show(
        {order: this.localOrder},
        {
          updated: (payload: Order) => {
            this.localOrder = payload;

            this.$core.dialogs.OrderRequisites.hide();
          },
          close: () => {
            // если это режим создания и пользователь закрывает карточку реквизитов заказа
            if (this.localOrder.isExisting === false) this.$core.dialogs.OrderCard.hide();
          }
        },
      );
    },

    /**
     * Открыть диалог массовой простановки дат и валюты для всех позиций заказа.
     * Дёргается, например, из шапки карточки заказа.
     */
    async showSetDeliveryDatesDialog(showCurrencyController: boolean = false): Promise<void> {
      await this.$core.dialogs.OrderPositionSetDeliveryDatesDialog.showAsPromise({
        order:            this.localOrder,
        showCurrencyCode: showCurrencyController,
        saveToServer:     false,
      });
    },

    onStartImport(): void {
      this.$core.dialogs.OrderPositionsClipboardImportDialog.show({ order: this.localOrder });
    },

    showOrderPositionsRegistryDialog(): void {
      const params: Record<string, any> = {
        seller:        this.localOrder.requisites.seller,
        isOfferBased:  false,
        orderCategory: this.localOrder.requisites.orderCategory,
      };

      if (this.localOrder.requisites.isPreorder === true) {
        // для предзаказа история — только по предзаказам
        params.isPreorder = true;
      }
      else {
        // обычный заказ — признак предзаказа не передаём
      }

      this.$core.dialogs.OrderPositionsRegistryDialog.show({
        title:         `История покупок ${this.localOrder.requisites.seller.toString()}`,
        targetDocument: this.localOrder,
        params,
        hiddenColumns: [
          'comment',
          'orderCategory',
          'seller',
          'buyer',
          'documentNumber',
          'desiredReceiptDate',
          'plannedShipmentDate',
          'payment',
          'status',
          'qtyInWork',
        ],
      });
    },

    /**
     * Утверждение заказа. Диалог закрывается только при успехе — иначе
     * пользователь остаётся в карточке и видит причину отказа.
     */
    async onConfirmButtonClicked(): Promise<void> {
      const success = await OrdersService.confirm(this.localOrder.id);

      if (success === true) {
        this.$core.dialogs.OrderCard.resolve();
      }
    },

    getFilterParamValue(key: string): any {
      return this.filterParams?.[key];
    },
    // ==================== Попапы ====================

    openAccountingSumBlockPopup($event: MouseEvent): void {
      this.$core.dialogs.OrderCurrencyRatesPopup.hideIfOpen()
      this.$core.dialogs.OrderCurrencyRatesPopup.show({
        order: this.localOrder,
        target: $event.target
      });
    },

    openSumBlockPopup($event: MouseEvent): void {
      this.$core.dialogs.OrderSumListPopup.hideIfOpen()
      this.$core.dialogs.OrderSumListPopup.show({
        order: this.localOrder,
        target: $event.target
      })
    },

    // ==================== Валидация ====================

    validateQty(value: number, position: OrderPosition): string | null {
      return this.$core.utils.number.validateDesiredQty(
        value,
        position.packageVolume,
        undefined,
        undefined,
        position.unit,
        this.localOrder.requisites.isRawMaterialsAndPackage,
      );
    },

    /**
     * Валидация-варнинг: проверяет, выпадает ли дата на выходной.
     * Контрол сам показывает варнинг в messages-row.
     */
    warnWeekend(value: unknown): string | null {
      if (!value) return null;
      if (this.$core.utils.date.isWeekend(value) === false) return null;
      return `Выпадает на выходной день (${this.$core.utils.date.getWeekDayName(value, true, true)})`;
    },

    /**
     * Helper-text для поля даты: показывает день недели обычного дня.
     */
    weekDayHelper(value: unknown): string {
      if (!value) return '';
      return this.$core.utils.date.getWeekDayName(value);
    },
  },
});
</script>
